import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { Input } from "@/components/ui-kit";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { User } from "@/types";
import { fmtDateTime, fmtDay } from "@/lib/time";
import { auditAction, auditRole, auditFacts } from "@/lib/auditLabels";
import { AuditPurge } from "@/components/AuditPurge";

interface Row { audit_id: string; action: string; actor_name: string; actor_role: string; target_type: string; target_id: string; details: Record<string, unknown>; ctx?: Record<string, unknown>; created_at: string }

const bgDay = (iso: string) => new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Baghdad" });
const hhmm = (iso: string, lang: string) => new Date(iso).toLocaleTimeString(lang === "ar" ? "ar-IQ" : "en-GB", { timeZone: "Asia/Baghdad", hour: "numeric", minute: "2-digit" });

const AuditRow = ({ a }: { a: Row }) => {
  const { lang } = usePrefs();
  const [open, setOpen] = useState(false);
  const facts = auditFacts(a.ctx, a.details, lang);
  const brief = facts.slice(0, 2).map((f) => f.value).join(" · ");
  return (
    <li data-testid={`audit-${a.audit_id}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="w-full text-start px-3 py-2.5 flex items-center gap-3 hover:bg-accent/50">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate" data-testid={`audit-action-${a.audit_id}`} title={a.action}>{auditAction(a.action, lang)}</p>
          <p className="text-xs text-muted-foreground truncate" data-testid={`audit-brief-${a.audit_id}`}>{brief && <><bdi>{brief}</bdi> · </>}<bdi>{a.actor_name}</bdi></p>
        </div>
        <span className="text-xs text-muted-foreground shrink-0" dir="ltr">{hhmm(a.created_at, lang)}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <dl data-testid={`audit-details-${a.audit_id}`} className="px-3 pb-3 text-xs grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          {facts.map((f, i) => <React.Fragment key={i}><dt className="text-muted-foreground">{f.label}</dt><dd className="font-semibold break-words" dir="auto">{f.value}</dd></React.Fragment>)}
          <dt className="text-muted-foreground">{lang === "ar" ? "بواسطة" : "By"}</dt><dd className="font-semibold">{a.actor_name} ({auditRole(a.actor_role, lang)})</dd>
          <dt className="text-muted-foreground">{lang === "ar" ? "الوقت" : "Time"}</dt><dd>{fmtDateTime(a.created_at, lang)}</dd>
        </dl>
      )}
    </li>
  );
};

export default function AdminAudit() {
  const { t, lang } = usePrefs();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const me = useAuth().user as User;
  const allowed = (me.admin_role || "super") === "super";
  const load = () => api.get("/admin/audit", { params: { limit: 500 } }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { if (allowed) load(); }, [allowed]); // eslint-disable-line react-hooks/exhaustive-deps
  const needle = q.trim().toLowerCase();
  const shown = needle ? rows.filter((a) => [auditAction(a.action, lang), a.action, a.actor_name, ...auditFacts(a.ctx, a.details, lang).map((f) => f.value)].some((x) => (x || "").toLowerCase().includes(needle))) : rows;
  if (!allowed) return <Navigate to="/admin/dashboard" replace />;
  const today = bgDay(new Date().toISOString());
  const yest = bgDay(new Date(Date.now() - 864e5).toISOString());
  const groups: [string, Row[]][] = [];
  shown.forEach((a) => {
    const d = bgDay(a.created_at);
    if (!groups.length || groups[groups.length - 1][0] !== d) groups.push([d, []]);
    groups[groups.length - 1][1].push(a);
  });
  return (
    <div data-testid="admin-audit-page">
      <PageTitle title={t("b7.auditTitle")} sub={t("b7.auditSub")} />
      <Input dir="auto" value={q} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)} placeholder={t("b7.filterAction")} data-testid="audit-filter" className="h-11 rounded-full max-w-sm mb-4" />
      <AuditPurge onPurged={load} />
      <div className="space-y-4">
        {groups.map(([d, list]) => (
          <section key={d} data-testid={`audit-day-${d}`}>
            <p className="text-xs font-bold text-muted-foreground mb-1.5 px-1">{d === today ? t("audit.today") : d === yest ? t("audit.yesterday") : fmtDay(d, lang)} · {list.length}</p>
            <ul className="rounded-2xl border bg-card divide-y overflow-hidden">{list.map((a) => <AuditRow key={a.audit_id} a={a} />)}</ul>
          </section>
        ))}
      </div>
    </div>
  );
}
